import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { api } from '@/utils/api';
import { ApiErrorResponse, ApiResponse } from '@/types/api';
import { Nullable } from '@/types/helper';

export type SortType = 'most' | 'recent' | 'name' | 'latestCreation';

/**
 * prefix 매칭용 키. 정렬·검색·페이지가 달라도 모든 고객 목록 캐시를 한 번에 무효화한다.
 * 좋아요 토글·고객 등록처럼 목록을 바꾸는 동작에서 반드시 무효화해야 한다
 * (staleTime이 걸려 있어 마운트마다 다시 받아오지 않는다).
 */
export const CLIENT_LIST_QUERY_KEY = ['getClientList'] as const;

export type ClientItem = {
  attentionRate: '높음' | '중간' | '낮음';
  id: number;
  isChecked: boolean;
  lastAction: Nullable<string>;
  like: boolean;
  name: Nullable<string>;
  company: Nullable<string>;
  phone: Nullable<string>;
  email?: Nullable<string>;
  /**
   * 수신거부·반송 이력으로 이메일 발송이 불가한 대상인지 (구 백엔드는 미제공).
   * 이메일 캠페인 수신자 선택에서만 사용한다 — 고객 목록·고객 편집 모달은 이 값을 무시하고
   * 기존과 동일하게 동작해야 한다(PDF·링크 공유는 계속 가능).
   */
  isEmailBlocked?: boolean;
  /** 차단 사유. 차단 대상이 아니면 null. 표시 문구에만 쓰고 판정은 isEmailBlocked로 한다 */
  emailBlockReason?: Nullable<string>;
};

export type LockedItem = { isLocked: true };

export const isLockedItem = (
  item: ClientItem | LockedItem,
): item is LockedItem => 'isLocked' in item;

export interface GetClientListRequest {
  sort: SortType;
  search?: string;
  /** 0부터 시작한다. 화면의 페이지 번호(1부터)와 다르니 변환해서 넘길 것. */
  page?: number;
  /**
   * 보내지 않으면 서버가 전체 목록을 반환한다.
   * 고객 편집 모달(링크·PDF 상세)과 캠페인 수신자 선택은 전량이 필요하므로
   * 그쪽에서는 limit을 넘기지 않는다. 기본값을 만들면 그 화면들이 조용히 잘린다.
   */
  limit?: number;
}

interface GetClientListResponse {
  clients: (ClientItem | LockedItem)[];
  /**
   * 검색·페이지네이션 지원 이전 백엔드에서는 내려오지 않는다.
   * 소비하는 쪽에서 `?? clients.length` 폴백을 반드시 적용할 것.
   */
  /** 검색 적용 후 건수 — 페이지네이션의 총 개수 */
  totalCount?: number;
  /** 검색과 무관한 전체 고객 수 — 플랜 카운터·상한 판정 기준 */
  allCount?: number;
  page?: number;
  /** 전체를 반환한 경우 null */
  limit?: Nullable<number>;
}

export const getClientList = ({
  sort,
  search,
  page,
  limit,
}: GetClientListRequest) => {
  // axios가 undefined 파라미터를 떨구므로 sort만 넘기는 호출부의 요청 URL은 그대로다.
  return api.get<ApiResponse<GetClientListResponse>>(`/client/list`, {
    params: { sort, search, page, limit },
  });
};

const useClientList = <
  TData = AxiosResponse<ApiResponse<GetClientListResponse>>,
  TError = AxiosError<ApiErrorResponse>,
>(
  request: GetClientListRequest,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<GetClientListResponse>>,
    TError,
    TData,
    ['getClientList', GetClientListRequest]
  >,
): UseQueryResult<TData, TError> => {
  return useQuery(['getClientList', request], () => getClientList(request), {
    /**
     * 목록 화면에서 페이지·검색어를 오갈 때마다 재요청하지 않도록 캐시를 유지한다.
     * 좋아요 토글과 고객 등록이 ['getClientList']를 무효화하므로 낡은 값이 남지 않는다.
     */
    staleTime: 30_000,
    ...options,
  });
};

export default useClientList;
